/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file favouriteSlice.js
 * @description Redux slice for managing favourite related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";
import { getAuthAccountId } from "../reselect/auth-selector";

/**
 * @function getInitialState
 * @description Creates the initial state object for the favourite slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (favourites = null) => ({
  error: null,
  favourites,
  loading: false,
});

/**
 * @function getFavourites
 * @description Async thunk for get favourites operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getFavourites = createAsyncThunk("favourites/loadFavourites", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/acc/${accountId}/favourites?originId=${originId}&lineId=${lineId}`);

    return {
      favourites: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain favourite details", // serializable (err.response.data)
      name: "Favourite List Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function addFavourite
 * @description Async thunk for add favourite operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const addFavourite = createAsyncThunk("favourites/addFavourite", async ({ eventId, eventPathId }, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    await axios.post(`/player/acc/${accountId}/favourites?originId=${originId}&lineId=${lineId}`, {
      eventId,
      eventPathId,
    });

    thunkAPI.dispatch(getFavourites()); // refresh cleanly after modifying...

    return {};
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to update favourites", // serializable (err.response.data)
      name: "Favourite Add Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function deleteFavourite
 * @description Async thunk for delete favourite operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const deleteFavourite = createAsyncThunk("favourites/deleteFavourite", async ({ id }, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    await axios.delete(`/player/acc/${accountId}/favourites/${id}?originId=${originId}&lineId=${lineId}`);

    thunkAPI.dispatch(getFavourites()); // refresh cleanly after modifying...

    return {};
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to delete favourites", // serializable (err.response.data)
      name: "Favourite Delete Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});
/**
 * @constant favouriteSlice
 * @description Redux slice for favourite management
 */
const favouriteSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getFavourites.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getFavourites.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getFavourites.fulfilled, (state, action) => {
        state.favourites = action.payload.favourites;
        state.error = null;
        state.loading = false;
      })
      .addCase(addFavourite.pending, (state) => {
        state.error = null;
      })
      .addCase(addFavourite.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(addFavourite.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(deleteFavourite.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteFavourite.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(deleteFavourite.fulfilled, (state) => {
        state.error = null;
      });
  },
  initialState: getInitialState(),
  name: "favourite",
  reducers: {},
});
const { reducer } = favouriteSlice;
export default reducer;
